import getConfig from 'next/config'
import { OneSearchClient } from '@awazevr/mfe-common.api.one-search-api'
import {
  CommonSearchResult,
  SearchCriteria,
} from '@awazevr/mfe-common.types-and-mocks.site-search-service'
import { SearchOptions } from './properties.types'
import { getCurrentPublicRuntimeConfig } from 'src/utils/helpers/helpers'
import { AwazeCountry } from 'src/utils/globalProps'
import { newrelicErrorHandler } from 'src/utils/errorHandler/newrelicErrorHandler'
import {
  SiteSearchService,
  SummariesApi,
} from '@awazevr/mfe-common.api.site-search-service'

const oneSearchApiKey = process.env.NEXT_PUBLIC_ONESEARCH_API_KEY
const oneSearchApiUrl = process.env.NEXT_PUBLIC_ONESEARCH_API_URL
const summariesApiKey = process.env.PDP_API_KEY
const summariesApiUrl = process.env.PDP_API_URL

export const oneSearchCall = async (
  search: SearchCriteria,
  options: SearchOptions & {
    currency: string
    country: AwazeCountry
    host: string
    experiments?: Record<string, string>
  },
): Promise<CommonSearchResult | null> => {
  const {
    brand,
    language,
    size,
    salesMarket,
    salt,
    currency,
    awazeClient,
    passRequestSize,
  } = options

  const {
    supportsBestMatchSorting,
    supportSalesMarketSearch,
    supportsAccommodationTypeSearch,
  } = getCurrentPublicRuntimeConfig()[brand]

  const {
    publicRuntimeConfig: { APP_ENV },
  } = getConfig()
  const OneSearchEnv = { dev: 'dev', pprd: 'pprd', prd: 'prod' }

  // If the search does not contain any of locationTag, regionId and placesId it's on purpose and return null
  // https://awazevr.atlassian.net/browse/APB-2684
  if (supportSalesMarketSearch && !search.placesId) {
    return Promise.resolve(null)
  }

  // Fix for ACQ-817. Wrapping the API call in a Promise.race to enforce the timeout
  const timeoutMs = 5000

  // Create a timeout promise
  const timeoutPromise = new Promise<null>((_, reject) => {
    setTimeout(
      () => reject(new Error('Timeout exceeded for oneSearchCall')),
      timeoutMs,
    )
  })

  const oneSearchService = new SiteSearchService(
    new OneSearchClient(
      oneSearchApiKey,
      oneSearchApiUrl,
      {
        client: 'landing-pages-mfe',
        currency: currency,
        environment: OneSearchEnv[APP_ENV] || 'dev',
        locale: language,
      },
      brand,
      {
        'x-awaze-user-agent': 'landing-pages-mfe',
        'x-awaze-bot-traffic': 'false',
        ...(awazeClient ? { 'x-awaze-client': awazeClient } : {}),
      },
    ),
    new SummariesApi(summariesApiKey, summariesApiUrl),
  )

  const oneSearchSortingRules = (sort: string | undefined) => {
    const sortLowerCased = sort?.toLowerCase()

    if (sortLowerCased === 'bestmatch') {
      return supportsBestMatchSorting ? 'recommended' : undefined
    }

    if (sortLowerCased === 'gradedesc' || sortLowerCased === 'gradedsc') {
      return 'recommended'
    }

    if (!sortLowerCased) {
      // If no sort is provided, default to 'recommended' if best match sorting is supported
      return supportsBestMatchSorting ? 'recommended' : undefined
    }

    return sort
  }

  try {
    // Wrap the API call in Promise.race to enforce the timeout
    const result = await Promise.race([
      oneSearchService.getListResults(
        {
          ...search,
          ...(passRequestSize && { size }),
          sref: salt || '',
          sort: oneSearchSortingRules(search.sort),
          salesMarket: supportSalesMarketSearch ? salesMarket : undefined,
          accommodationType:
            search.accommodationType ||
            (supportsAccommodationTypeSearch ? 'cottages' : undefined),
        },
        brand,
        language,
        ['DISPLAY_NAME', 'IMAGES', 'USPS', 'REVIEW'],
      ),
      timeoutPromise, // Include the timeout promise
    ])

    // Slice and return the properties, or null if the result is empty
    const { properties, ...rest } = result
    return { properties: properties?.slice(0, size) || [], ...rest }
  } catch (error) {
    newrelicErrorHandler(error.message || 'Error in oneSearchCall')
    return null
  }
}
